import { useEffect, useMemo, useState } from "react";
import AuthClient from "../api/AuthClient";
import MissingClubsForPromoterGroupApi, {
  MissingClubApiItem,
} from "../api/MissingClubsForPromoterGroupApi";
import { PartyModel } from "../types";

export type MissingClubsRequestState = "idle" | "loading" | "success" | "error";

const inFlightMissingClubRequests = new Map<string, Promise<MissingClubApiItem[]>>();

export const fetchPromoterMissingClubs = async (
  group: PartyModel
): Promise<MissingClubApiItem[]> => {
  const groupId = group.party.id;
  const existingRequest = inFlightMissingClubRequests.get(groupId);

  if (existingRequest) {
    return existingRequest;
  }

  const request = (async () => {
    const api = new MissingClubsForPromoterGroupApi(new AuthClient());
    const response = await api.getMissingClubs(
      group.party.city,
      group.party.partyDate,
      group.party.bookingType
    );

    return response?.data?.missingClubs ?? [];
  })();

  inFlightMissingClubRequests.set(groupId, request);

  try {
    return await request;
  } finally {
    inFlightMissingClubRequests.delete(groupId);
  }
};

export const usePromoterMissingClubs = (group: PartyModel | null) => {
  const [missingClubs, setMissingClubs] = useState<MissingClubApiItem[]>([]);
  const [requestState, setRequestState] = useState<MissingClubsRequestState>("idle");

  useEffect(() => {
    if (!group) {
      setMissingClubs([]);
      setRequestState("idle");
      return;
    }

    let isCancelled = false;

    const loadMissingClubs = async () => {
      setRequestState("loading");
      setMissingClubs([]);

      try {
        const clubs = await fetchPromoterMissingClubs(group);

        if (isCancelled) {
          return;
        }

        setMissingClubs(clubs);
        setRequestState("success");
      } catch (error) {
        if (!isCancelled) {
          console.error("Error fetching missing clubs:", error);
          setMissingClubs([]);
          setRequestState("error");
        }
      }
    };

    void loadMissingClubs();

    return () => {
      isCancelled = true;
    };
  }, [group]);

  return useMemo(
    () => ({
      missingClubs,
      requestState,
    }),
    [missingClubs, requestState]
  );
};
